import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    App,
    Button,
    Card,
    Descriptions,
    Empty,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Timeline,
    Typography
} from 'antd';
import {PlayCircleOutlined, ReloadOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {AgentDebugExecuteRequest, AgentDebugRecord, agentDebugService} from '../../services/agentDebugService';
import {executionService} from '../../services/executionService';
import {AgentExecutionRunDetail, AgentExecutionTurn} from '../../types/agent';
import dayjs from 'dayjs';

const {Title, Text} = Typography;
const {TextArea} = Input;

const TRIGGER_KINDS = [
    {value: 'EVENT', label: 'EVENT'},
    {value: 'MESSAGE', label: 'MESSAGE'},
];

const SIDE_EFFECT_MODES = [
    {value: 'SAFE', label: 'SAFE（安全模式）'},
    {value: 'READ_ONLY', label: 'READ_ONLY（只读模式）'},
    {value: 'LIVE', label: 'LIVE（实时模式）'},
];

const REPLAY_SOURCE_KINDS = [
    {value: 'EVENT', label: 'EVENT'},
    {value: 'MESSAGE', label: 'MESSAGE'},
    {value: 'CHAT', label: 'CHAT'},
    {value: 'AGENT_CALL', label: 'AGENT_CALL'},
    {value: 'TASK_PLAN', label: 'TASK_PLAN'},
];

const getStatusColor = (status: string): string => {
    switch (status) {
        case 'COMPLETED':
            return 'green';
        case 'RUNNING':
            return 'blue';
        case 'FAILED':
            return 'red';
        case 'SUSPENDED':
            return 'orange';
        case 'SUBMITTED':
            return 'gold';
        default:
            return 'default';
    }
};

const AgentDebugPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [history, setHistory] = useState<AgentDebugRecord[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<AgentDebugRecord | null>(null);
    const [detail, setDetail] = useState<AgentExecutionRunDetail | null>(null);
    const [turns, setTurns] = useState<AgentExecutionTurn[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [expandedTurn, setExpandedTurn] = useState<number | null>(null);

    const [manualForm] = Form.useForm();
    const [replayForm] = Form.useForm();
    const [executing, setExecuting] = useState(false);

    const loadHistory = async (agentConfigId: number) => {
        setHistoryLoading(true);
        try {
            const data = await agentDebugService.getHistory(agentConfigId, 50);
            setHistory(data);
        } catch {
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        loadHistory(1);
    }, []);

    const handleManualDebug = async () => {
        try {
            const values = await manualForm.validateFields();
            setExecuting(true);
            const request: AgentDebugExecuteRequest = {
                agentConfigId: values.agentConfigId,
                triggerKind: values.triggerKind,
                eventType: values.eventType || '',
                payloadJson: values.payloadJson,
                sideEffectMode: values.sideEffectMode,
                skipStartingGun: values.skipStartingGun,
            };
            const response = await agentDebugService.executeDebug(request);
            messageApi.success(`调试已提交: ${response.status} - ${response.message}`);
            await loadHistory(values.agentConfigId);
            if (response.recordId) {
                try {
                    await agentDebugService.getDetail(response.recordId);
                } catch {
                    // 静默处理
                }
            }
        } catch (e: any) {
            if (e?.errorFields) return;
            messageApi.error(e?.message || '调试执行失败');
        } finally {
            setExecuting(false);
        }
    };

    const handleReplayFromSource = async () => {
        try {
            const values = await replayForm.validateFields();
            setExecuting(true);
            const request: AgentDebugExecuteRequest = {
                agentConfigId: values.agentConfigId,
                triggerKind: values.triggerKind || 'EVENT',
                eventType: '',
                payloadJson: '{}',
                replaySourceKind: values.replaySourceKind,
                replaySourceId: values.replaySourceId,
                sideEffectMode: values.sideEffectMode || 'SAFE',
            };
            const response = await agentDebugService.executeDebug(request);
            messageApi.success(`重放已提交: ${response.status} - ${response.message}`);
            await loadHistory(values.agentConfigId);
        } catch (e: any) {
            if (e?.errorFields) return;
            messageApi.error(e?.message || '重放执行失败');
        } finally {
            setExecuting(false);
        }
    };

    const handleRecordClick = async (record: AgentDebugRecord) => {
        setSelectedRecord(record);
        if (record.executionId) {
            setDetailLoading(true);
            try {
                const [detailData, turnsData] = await Promise.all([
                    executionService.getAgentExecution(record.executionId),
                    executionService.getAgentExecutionTurns(record.executionId),
                ]);
                setDetail(detailData);
                setTurns(turnsData);
            } catch {
                setDetail(null);
                setTurns([]);
            } finally {
                setDetailLoading(false);
            }
        } else {
            setDetail(null);
            setTurns([]);
        }
    };

    const historyColumns: ColumnsType<AgentDebugRecord> = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 60},
        {title: '智能体', dataIndex: 'agentName', key: 'agentName', width: 120},
        {
            title: '触发', key: 'trigger', width: 120,
            render: (_, r) => (
                <Space size={4}>
                    <Tag>{r.triggerKind}</Tag>
                    {r.eventType && <Tag color="cyan">{r.eventType}</Tag>}
                </Space>
            )
        },
        {
            title: '执行ID', dataIndex: 'executionId', key: 'executionId', width: 160,
            render: (v: string) => v ? <Text code style={{fontSize: 11}}>{v.substring(0, 16)}...</Text> : '-'
        },
        {
            title: '状态', dataIndex: 'status', key: 'status', width: 100,
            render: (v: string) => <Tag color={getStatusColor(v)}>{v}</Tag>
        },
        {title: '耗时', dataIndex: 'totalTimeMs', key: 'totalTimeMs', width: 80, render: (v: number) => `${v}ms`},
        {
            title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160,
            render: (v: string) => v ? dayjs(v).format('MM-DD HH:mm:ss') : '-'
        },
        {
            title: '操作', key: 'action', width: 120,
            render: (_: unknown, record: AgentDebugRecord) => (
                <Space>
                    <Button type="link" size="small" onClick={() => handleRecordClick(record)}>查看</Button>
                    {record.executionId && (
                        <Button type="link" size="small"
                                onClick={() => navigate(`/executions/${record.executionId}`)}>
                            执行详情
                        </Button>
                    )}
                </Space>
            )
        },
    ];

    return (
        <div>
            <Title level={2} style={{marginBottom: 24}}>智能体调试</Title>

            {/* 表单区域 */}
            <div style={{display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap'}}>
                {/* Manual Debug 表单 */}
                <Card title="手动调试" style={{flex: '1 1 400px', minWidth: 400}}>
                    <Form form={manualForm} layout="vertical" size="small"
                          initialValues={{
                              triggerKind: 'EVENT',
                              sideEffectMode: 'SAFE',
                              skipStartingGun: false,
                              agentConfigId: 1
                          }}>
                        <Form.Item name="agentConfigId" label="智能体ID" rules={[{required: true}]}>
                            <InputNumber min={1} style={{width: '100%'}}/>
                        </Form.Item>
                        <Form.Item name="triggerKind" label="触发类型" rules={[{required: true}]}>
                            <Select options={TRIGGER_KINDS}/>
                        </Form.Item>
                        <Form.Item name="eventType" label="事件/消息类型">
                            <Input placeholder="如：MARKET_ANALYSIS"/>
                        </Form.Item>
                        <Form.Item name="payloadJson" label="载荷JSON" rules={[{required: true}]}>
                            <TextArea rows={4} placeholder='{"key":"value"}'/>
                        </Form.Item>
                        <Form.Item name="sideEffectMode" label="副作用模式">
                            <Select options={SIDE_EFFECT_MODES}/>
                        </Form.Item>
                        <Form.Item name="skipStartingGun" label="跳过发令枪" valuePropName="checked">
                            <Select options={[{value: true, label: '是'}, {value: false, label: '否'}]}/>
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" icon={<PlayCircleOutlined/>} loading={executing}
                                    onClick={handleManualDebug} block>
                                执行调试
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

                {/* Replay from Source 表单 */}
                <Card title="从来源重放" style={{flex: '1 1 400px', minWidth: 400}}>
                    <Form form={replayForm} layout="vertical" size="small"
                          initialValues={{replaySourceKind: 'EVENT', sideEffectMode: 'SAFE', agentConfigId: 1}}>
                        <Form.Item name="agentConfigId" label="智能体ID" rules={[{required: true}]}>
                            <InputNumber min={1} style={{width: '100%'}}/>
                        </Form.Item>
                        <Form.Item name="replaySourceKind" label="来源类型" rules={[{required: true}]}>
                            <Select options={REPLAY_SOURCE_KINDS}/>
                        </Form.Item>
                        <Form.Item name="replaySourceId" label="来源ID" rules={[{required: true}]}>
                            <Input placeholder="输入来源ID"/>
                        </Form.Item>
                        <Form.Item name="sideEffectMode" label="副作用模式">
                            <Select options={SIDE_EFFECT_MODES}/>
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" icon={<ReloadOutlined/>} loading={executing}
                                    onClick={handleReplayFromSource} block>
                                从来源重放
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>

            {/* 调试历史列表 */}
            <Card title="调试历史" style={{marginBottom: 16}}>
                <Button size="small" icon={<ReloadOutlined/>} onClick={() => loadHistory(1)}
                        style={{marginBottom: 12}}>刷新</Button>
                <Table
                    rowKey="id"
                    columns={historyColumns}
                    dataSource={history}
                    loading={historyLoading}
                    pagination={{pageSize: 10}}
                    scroll={{x: 'max-content'}}
                    size="small"
                />
            </Card>

            {/* 选中记录的三面板详情 */}
            {selectedRecord && (
                <div>
                    {detailLoading ? (
                        <div style={{display: 'flex', justifyContent: 'center', padding: 40}}>
                            <Spin/>
                        </div>
                    ) : detail ? (
                        <>
                            {/* 顶部面板：触发信息 */}
                            <Card title="触发信息" style={{marginBottom: 16}}>
                                <Descriptions column={3} bordered size="small">
                                    <Descriptions.Item label="执行ID">{detail.executionId}</Descriptions.Item>
                                    <Descriptions.Item label="Trace ID">{detail.traceId}</Descriptions.Item>
                                    <Descriptions.Item label="智能体">{detail.agentName}</Descriptions.Item>
                                    <Descriptions.Item label="触发类型">
                                        <Tag>{detail.triggerKind}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="触发码">{detail.triggerCode || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="触发ID">{detail.triggerId || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="副作用模式">
                                        <Tag color={detail.sideEffectMode === 'LIVE' ? 'red' : 'orange'}>
                                            {detail.sideEffectMode || '-'}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="恢复模式">
                                        {detail.resumeMode ? <Tag color="purple">RESUME</Tag> : <Tag>NORMAL</Tag>}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="状态">
                                        <Tag color={getStatusColor(detail.status)}>{detail.status}</Tag>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {/* 中间面板：Turns时间线 */}
                            <Card title={`Turns时间线 (${turns.length})`} style={{marginBottom: 16}}>
                                {turns.length === 0 ? (
                                    <Empty description="无Turn数据"/>
                                ) : (
                                    <Timeline
                                        items={turns.map((turn) => ({
                                            color: turn.errorMessage ? 'red' : 'blue',
                                            children: (
                                                <div style={{cursor: 'pointer'}}
                                                     onClick={() => setExpandedTurn(expandedTurn === turn.turnNo ? null : turn.turnNo)}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <Text strong>Turn {turn.turnNo}</Text>
                                                        <Tag>{turn.phase}</Tag>
                                                        {turn.responseType && <Tag>{turn.responseType}</Tag>}
                                                        {turn.functionName &&
                                                            <Tag color="cyan">{turn.functionName}</Tag>}
                                                        {null != turn.durationMs &&
                                                            <Text type="secondary">{turn.durationMs}ms</Text>}
                                                    </div>
                                                    {expandedTurn === turn.turnNo && (
                                                        <div style={{marginTop: 8, paddingLeft: 16}}>
                                                            <Descriptions column={2} size="small" bordered>
                                                                <Descriptions.Item
                                                                    label="模型ID">{turn.modelId || '-'}</Descriptions.Item>
                                                                <Descriptions.Item
                                                                    label="工具选择">{turn.toolChoice || '-'}</Descriptions.Item>
                                                                <Descriptions.Item
                                                                    label="输出类型">{turn.outputKind || '-'}</Descriptions.Item>
                                                                <Descriptions.Item
                                                                    label="输出码">{turn.outputCode || '-'}</Descriptions.Item>
                                                                <Descriptions.Item label="Token使用" span={2}>
                                                                    {turn.tokenUsageJson || '-'}
                                                                </Descriptions.Item>
                                                                {turn.errorMessage && (
                                                                    <Descriptions.Item label="错误信息" span={2}>
                                                                        <Text type="danger">{turn.errorMessage}</Text>
                                                                    </Descriptions.Item>
                                                                )}
                                                            </Descriptions>
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        }))}
                                    />
                                )}
                            </Card>

                            {/* 底部面板：消息/输出 */}
                            <Card title="消息/输出">
                                <Descriptions column={2} bordered size="small">
                                    <Descriptions.Item label="最终输出类型">
                                        {detail.finalOutputKind ?
                                            <Tag color="green">{detail.finalOutputKind}</Tag> : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label="最终输出码">{detail.finalOutputCode || '-'}</Descriptions.Item>
                                    <Descriptions.Item
                                        label="挂起原因">{detail.suspendReason || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Turn进度">
                                        {detail.currentTurn} / {detail.totalTurns}
                                    </Descriptions.Item>
                                    {detail.errorMessage && (
                                        <Descriptions.Item label="错误信息" span={2}>
                                            <Text type="danger">{detail.errorMessage}</Text>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                            </Card>
                        </>
                    ) : (
                        <Card>
                            <Empty description="该调试记录无关联的执行实例"/>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
};

export default AgentDebugPage;